import React, { useEffect, useRef, useState } from "react";
import { DatePicker, Input, Select, Tabs } from "antd";
import moment from "moment";
import TableWithSkeleton from "../../../../components/global/table/TableWithSkeleton.jsx";
import Pagination from "../../../../components/global/pagination/Pagination.jsx";
import UserAvatar from "../../../../components/global/avtar/UserAvatar.jsx";
import openTradesColumns from "../../../../columns/salesDashboard/openTrades.js";
import closedTradesColumns from "../../../../columns/salesDashboard/closedTrades.js";
import {
  GetAllOpenTradesApi,
  GetAllClosedTradesApi,
} from "../../../../api/request/account.js";
import { GetAllTraderApi } from "../../../../api/request/client.js";
import { useUserStore } from "../../../../store/userStore.js";

const { RangePicker } = DatePicker;

const TAB_OPEN = "open";
const TAB_CLOSED = "closed";

const formatProfit = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "--";
  const formatted = num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return (
    <span className={num >= 0 ? "text-green-600" : "text-red-500"}>
      {formatted}
    </span>
  );
};

export default function TradesSection() {
  const userDetails = useUserStore((state) => state.userDetails);
  const [activeTab, setActiveTab] = useState(TAB_OPEN);
  const [dates, setDates] = useState([]);
  const [search, setSearch] = useState("");
  const [searchText, setSearchText] = useState("");
  const [selectedUserEmail, setSelectedUserEmail] = useState(undefined);
  const [userOptions, setUserOptions] = useState([]);
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecord, setTotalRecord] = useState(0);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tableKey, setTableKey] = useState(0);
  const requestIdRef = useRef(0);

  const isOpenTab = activeTab === TAB_OPEN;

  const getUsers = async () => {
    setIsUsersLoading(true);
    try {
      const params = {
        ApplyPagination: false,
        SalesManId: userDetails?.userId,
      };
      const { data: response } = await GetAllTraderApi(
        new URLSearchParams(params).toString(),
      );
      if (response?.success) {
        setUserOptions(
          (response?.data || [])
            .filter((user) => user?.email)
            .map((user) => {
              const name =
                user?.name ||
                `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
                "--";
              return {
                label: `${name} (${user.email})`,
                value: user.email,
              };
            }),
        );
      } else {
        setUserOptions([]);
      }
    } catch (error) {
      setUserOptions([]);
    } finally {
      setIsUsersLoading(false);
    }
  };

  const mapTradeRow = (item, index, forOpenTab) => {
    const name =
      item?.name ||
      `${item?.firstName || ""} ${item?.lastName || ""}`.trim() ||
      "--";

    const common = {
      ...item,
      key: `${item?.positionId || item?.dealId || index}-${item?.login || ""}`,
      user: (
        <UserAvatar
          data={{
            name,
            email: item?.email,
          }}
        />
      ),
      profit: formatProfit(item?.profit),
    };

    if (forOpenTab) {
      return {
        ...common,
        time: item?.time
          ? moment(item.time).format("DD-MM-YYYY, hh:mm A")
          : "--",
      };
    }

    return {
      ...common,
      openTime: item?.openTime
        ? moment(item.openTime).format("DD-MM-YYYY, hh:mm A")
        : "--",
      closeTime: item?.closeTime
        ? moment(item.closeTime).format("DD-MM-YYYY, hh:mm A")
        : "--",
    };
  };

  const getTrades = async ({
    tab,
    pageNumber,
    size,
    searchValue,
    dateRange,
    filterText,
  }) => {
    const requestId = ++requestIdRef.current;
    const forOpenTab = tab === TAB_OPEN;

    setIsLoading(true);
    setData([]);

    try {
      const params = {
        PageNumber: pageNumber,
        PageSize: size,
        SearchText: searchValue,
        FilterText: filterText || "",
        SortBy: forOpenTab ? "Time" : "CloseTime",
        FromDate:
          dateRange.length === 2 ? dateRange[0].format("MM-DD-YYYY") : "",
        ToDate:
          dateRange.length === 2 ? dateRange[1].format("MM-DD-YYYY") : "",
      };

      const api = forOpenTab ? GetAllOpenTradesApi : GetAllClosedTradesApi;
      const { data: response } = await api(
        new URLSearchParams(params).toString(),
      );

      // Ignore outdated responses from previous tab/filter requests
      if (requestId !== requestIdRef.current) return;

      if (response?.success) {
        setData(
          (response?.data || []).map((item, index) =>
            mapTradeRow(item, index, forOpenTab),
          ),
        );
        setTotalRecord(response?.totalRecords || 0);
      } else {
        setData([]);
        setTotalRecord(0);
      }
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      setData([]);
      setTotalRecord(0);
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!userDetails?.userId) return;
    getUsers();
  }, [userDetails?.userId]);

  useEffect(() => {
    getTrades({
      tab: activeTab,
      pageNumber: page,
      size: pageSize,
      searchValue: searchText,
      dateRange: dates,
      filterText: selectedUserEmail,
    });
  }, [page, pageSize, dates, searchText, selectedUserEmail, activeTab]);

  useEffect(() => {
    setPage(1);
  }, [dates, searchText, selectedUserEmail]);

  const handleTabChange = (key) => {
    requestIdRef.current += 1;
    setIsLoading(true);
    setData([]);
    setTotalRecord(0);
    setTableKey((prev) => prev + 1);
    setActiveTab(key);
    setDates([]);
    setSearch("");
    setSearchText("");
    setSelectedUserEmail(undefined);
    setPage(1);
  };

  return (
    <div className="border-2 border-light rounded-[10px] p-5 overflow-hidden">
      <div className="flex justify-between flex-wrap gap-5 items-center mb-4">
        <div>
          <h2 className="text-title font-semibold">Trades</h2>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select
            allowClear
            showSearch
            loading={isUsersLoading}
            placeholder="Select user"
            style={{ width: 420 }}
            className="ant-select-selector-white"
            popupMatchSelectWidth={false}
            dropdownStyle={{ minWidth: 420 }}
            value={selectedUserEmail}
            options={userOptions}
            optionFilterProp="label"
            filterOption={(input, option) =>
              String(option?.label ?? "")
                .toLowerCase()
                .includes(String(input).toLowerCase())
            }
            onChange={(value) => setSelectedUserEmail(value)}
          />
          <RangePicker
            className="py-1.5 rounded-lg"
            format="DD-MM-YYYY"
            value={dates}
            onChange={(value) => setDates(value || [])}
          />
          <Input
            size="large"
            style={{ width: 220 }}
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={() => setSearchText(search)}
            prefix={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-5 h-5"
              >
                <path
                  fillRule="evenodd"
                  d="M10.5 3.75a6.75 6.75 0 1 0 0 13.5 6.75 6.75 0 0 0 0-13.5ZM2.25 10.5a8.25 8.25 0 1 1 14.59 5.28l4.69 4.69a.75.75 0 1 1-1.06 1.06l-4.69-4.69A8.25 8.25 0 0 1 2.25 10.5Z"
                  clipRule="evenodd"
                />
              </svg>
            }
            suffix={
              search && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  onClick={() => {
                    setSearch("");
                    setSearchText("");
                  }}
                  viewBox="0 0 24 24"
                  fill="#bebebe"
                  className="size-5 cursor-pointer duration-300 hover:fill-[#969595]"
                >
                  <path
                    fillRule="evenodd"
                    d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z"
                    clipRule="evenodd"
                  />
                </svg>
              )
            }
          />
        </div>
      </div>

      <Tabs
        className="mb-4"
        activeKey={activeTab}
        onChange={handleTabChange}
        items={[
          { label: "Open Trades", key: TAB_OPEN },
          { label: "Closed Trades", key: TAB_CLOSED },
        ]}
      />

      <div className="overflow-hidden rounded-[10px]">
        <TableWithSkeleton
          key={`${activeTab}-${tableKey}`}
          columns={isOpenTab ? openTradesColumns : closedTradesColumns}
          data={isLoading ? [] : data}
          loading={isLoading}
          page={page}
          pageSize={pageSize}
          length={pageSize}
          showIndexColumn={false}
        />
      </div>
      <Pagination
        total={totalRecord}
        pageSize={pageSize}
        setPageSize={setPageSize}
        page={page}
        setPage={setPage}
      />
    </div>
  );
}
